import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import {
  runAction, showProgressModal, hideProgressModal,
  addProgressStep, updateProgressBar, buildSubscriptionOptionsHtml,
} from "../js/admin_action_runner"

export default class extends Controller {
  static targets = [
    "progressModal", "progressTitle", "progressSteps", "progressBar",
    "subscriptionSelector", "subscriptionOptions",
    "actionStatus", "statusContent",
  ]

  static values = { auditUrl: String, executeUrl: String }

  connect() {
    this.subscriptionSelectorData = null
  }

  auditUser(event) {
    const userId = event.currentTarget.dataset.userId
    const button = document.getElementById(`audit-btn-${userId}`)

    button.innerHTML =
      '<div class="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin inline mr-1"></div>Checking...'
    button.disabled = true

    fetch(this.auditUrlValue.replace("USER_ID", userId), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').getAttribute("content"),
      },
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.success && result.user_data) {
          this._showUserActions(userId, result.user_data)
          button.classList.add("hidden")
        } else {
          button.textContent = "No Issues"
          button.className = "bg-green-600 text-white px-3 py-1 rounded-md text-xs"
          button.disabled = true
        }
      })
      .catch(() => {
        button.textContent = "Error"
        button.className = "bg-red-600 text-white px-3 py-1 rounded-md text-xs"
      })
  }

  _showUserActions(userId, userData) {
    const actionsDiv = document.getElementById(`user-actions-${userId}`)
    let html = ""

    if (userData.has_multiple_options) {
      html += `
        <button data-action="admin-subscription-audit#showCustomerOptions"
                data-user-id="${userId}"
                data-user-data='${JSON.stringify(userData).replace(/'/g, "&#39;")}'
                class="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-xs">
          Select Customer (${userData.customer_options.length} options)
        </button>`
    } else {
      if (userData.recommended_action.startsWith("CREATE_SUBSCRIPTION")) {
        const subs = userData.missing_subscriptions
        if (subs.length === 1) {
          html += `
          <button data-action="admin-subscription-audit#executeAction"
                  data-action-type="create_database_subscription"
                  data-action-data='{"user_id": ${userData.user_id}, "stripe_subscription_id": "${subs[0].stripe_subscription_id}", "stripe_customer_id": "${userData.stripe_customer_id}"}'
                  class="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 text-xs">
            Create${userData.match_type !== "exact_email" ? " (Domain Match)" : ""}
          </button>`
        } else {
          const subIds = subs.map((s) => s.stripe_subscription_id)
          html += `
          <button data-action="admin-subscription-audit#executeAction"
                  data-action-type="create_database_subscription"
                  data-action-data='{"user_id": ${userData.user_id}, "multiple_subscriptions": true, "stripe_subscriptions": ${JSON.stringify(subIds)}, "stripe_customer_id": "${userData.stripe_customer_id}"}'
                  class="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-xs">
            Select
          </button>`
        }
      }

      if (userData.user_stripe_customer_id !== userData.stripe_customer_id) {
        html += `
        <button data-action="admin-subscription-audit#executeAction"
                data-action-type="link_stripe_customer"
                data-action-data='{"user_id": ${userData.user_id}, "stripe_customer_id": "${userData.stripe_customer_id}"}'
                class="bg-purple-600 text-white px-3 py-1 rounded-md hover:bg-purple-700 text-xs ml-2">
          Link
        </button>`
      }

      html += `
      <button data-action="admin-subscription-audit#ignoreUser" data-user-id="${userId}"
              class="bg-gray-300 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-400 text-xs ml-2">
        Ignore
      </button>`
    }

    actionsDiv.innerHTML = html
    actionsDiv.classList.remove("hidden")
    this._showUserDetails(userId, userData)
  }

  _showUserDetails(userId, userData) {
    const userRow = document.getElementById(`user-row-${userId}`)
    const existing = document.getElementById(`user-details-${userId}`)
    if (existing) existing.remove()

    const detailsRow = document.createElement("tr")
    detailsRow.id = `user-details-${userId}`

    let content
    if (userData.has_multiple_options) {
      content = this._buildMultipleOptionsHtml(userData)
    } else {
      content = this._buildMissingSubsHtml(userData)
    }

    detailsRow.innerHTML = `<td colspan="5" class="px-6 py-4 bg-blue-50">${content}</td>`
    userRow.parentNode.insertBefore(detailsRow, userRow.nextSibling)
  }

  _buildMultipleOptionsHtml(userData) {
    const cards = userData.customer_options
      .map(
        (option) => `
      <div class="bg-white border border-gray-200 rounded-lg p-3">
        <div class="flex items-center justify-between mb-2">
          <div class="font-medium text-gray-900">${option.stripe_customer_email}</div>
          <span class="px-2 py-1 text-xs font-semibold rounded-full ${option.match_type === "exact_email" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}">
            ${option.match_type === "exact_email" ? "EXACT" : "DOMAIN"}
          </span>
        </div>
        <div class="text-xs text-gray-600 mb-2">Customer: ${option.stripe_customer_id}</div>
        <div class="space-y-1">
          ${option.subscriptions.map((sub) => `<div class="text-xs text-gray-500">${sub.stripe_subscription_id} - ${sub.status.toUpperCase()}</div>`).join("")}
        </div>
      </div>`
      )
      .join("")

    return `
      <div class="space-y-3">
        <div class="flex items-center space-x-2">
          <span class="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Multiple Customer Options</span>
          <span class="text-sm text-gray-600">${userData.customer_options.length} potential matches</span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">${cards}</div>
      </div>`
  }

  _buildMissingSubsHtml(userData) {
    const matchIndicator =
      userData.match_type && userData.match_type !== "exact_email"
        ? ` <span class="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">${userData.match_type.toUpperCase()}</span>`
        : ""

    const cards = userData.missing_subscriptions
      .map(
        (sub) => `
      <div class="bg-white border border-gray-200 rounded-lg p-3">
        <div class="flex items-center justify-between mb-2">
          <div class="font-medium text-gray-900">${sub.stripe_subscription_id}</div>
          <span class="px-2 py-1 text-xs font-semibold rounded-full ${sub.status === "active" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}">
            ${sub.status.toUpperCase()}
          </span>
        </div>
        <div class="text-xs text-gray-600">
          <div>Period: ${new Date(sub.current_period_start).toLocaleDateString()} - ${new Date(sub.current_period_end).toLocaleDateString()}</div>
          <div>Created: ${new Date(sub.created).toLocaleDateString()}</div>
        </div>
      </div>`
      )
      .join("")

    const customerNote =
      userData.stripe_customer_email && userData.stripe_customer_email !== userData.user_email
        ? `<div class="text-sm text-gray-600">Stripe Customer: ${userData.stripe_customer_email}</div>`
        : ""

    return `
      <div class="space-y-3">
        <div class="flex items-center space-x-2">
          <span class="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Missing Subscriptions</span>
          <span class="text-sm text-gray-600">${userData.missing_subscriptions.length} Stripe subscription(s)</span>
          ${matchIndicator}
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">${cards}</div>
        ${customerNote}
      </div>`
  }

  ignoreUser(event) {
    const userId = event.currentTarget.dataset.userId
    const userRow = document.getElementById(`user-row-${userId}`)
    const detailsRow = document.getElementById(`user-details-${userId}`)

    userRow.style.opacity = "0.5"
    if (detailsRow) detailsRow.style.opacity = "0.5"

    const button = document.getElementById(`audit-btn-${userId}`)
    button.textContent = "Ignored"
    button.className = "bg-gray-400 text-white px-3 py-1 rounded-md text-xs"
    button.disabled = true
  }

  executeAction(event) {
    const button = event.currentTarget
    let actionData
    try {
      actionData = JSON.parse(button.dataset.actionData)
    } catch {
      return
    }
    this._runAction(button.dataset.actionType, actionData)
  }

  _runAction(actionType, actionData) {
    const modal = this.progressModalTarget
    const title = this.progressTitleTarget
    const steps = this.progressStepsTarget
    const bar = this.progressBarTarget

    showProgressModal(modal, title, steps, bar, actionType)
    addProgressStep(steps, "Starting action...", "in-progress")
    updateProgressBar(bar, 20)

    runAction(this.executeUrlValue, actionType, actionData, {
      onStart: () => {},
      onSelection: (result) => {
        addProgressStep(steps, "Multiple subscriptions found", "completed")
        updateProgressBar(bar, 100)
        setTimeout(() => {
          hideProgressModal(modal)
          this._showSelector(result)
        }, 1000)
      },
      onSuccess: (result) => {
        addProgressStep(steps, result.message, "completed")
        updateProgressBar(bar, 100)
        setTimeout(() => {
          hideProgressModal(modal)
          this._showStatus(result, true)
          setTimeout(() => Turbo.visit(window.location.href, { action: "replace" }), 2000)
        }, 1500)
      },
      onError: (result) => {
        addProgressStep(steps, result.message, "error")
        updateProgressBar(bar, 100)
        setTimeout(() => {
          hideProgressModal(modal)
          this._showStatus(result, false)
        }, 3000)
      },
    })
  }

  _showStatus(result, success) {
    const statusClass = success
      ? "bg-green-50 border-green-200 text-green-800"
      : "bg-red-50 border-red-200 text-red-800"

    this.statusContentTarget.innerHTML = `
      <div class="border rounded-lg p-4 ${statusClass}">
        <div>
          <h4 class="font-semibold">${success ? "Action Completed" : "Action Failed"}</h4>
          <p class="text-sm mt-1">${result.message}</p>
        </div>
      </div>`

    this.actionStatusTarget.classList.remove("hidden")
  }

  _showSelector(result) {
    this.subscriptionSelectorData = result.data
    this.subscriptionOptionsTarget.innerHTML =
      buildSubscriptionOptionsHtml(result.subscription_options, "admin-subscription-audit")
    this.subscriptionSelectorTarget.classList.remove("hidden")
  }

  selectSubscription(event) {
    this.subscriptionSelectorTarget.classList.add("hidden")
    const actionData = Object.assign({}, this.subscriptionSelectorData, {
      stripe_subscription_id: event.currentTarget.dataset.subscriptionId,
    })
    this._runAction("create_database_subscription", actionData)
  }

  cancelSelection() {
    this.subscriptionSelectorTarget.classList.add("hidden")
  }

  showCustomerOptions(event) {
    const userData = JSON.parse(event.currentTarget.dataset.userData)
    if (!userData.customer_options.length) return

    this._showUserActions(event.currentTarget.dataset.userId, {
      user_id: userData.customer_options[0].user_id || event.currentTarget.dataset.userId,
      stripe_customer_id: userData.customer_options[0].stripe_customer_id,
      stripe_customer_email: userData.customer_options[0].stripe_customer_email,
      match_type: userData.customer_options[0].match_type,
      missing_subscriptions: userData.customer_options[0].subscriptions,
      recommended_action:
        userData.customer_options[0].subscriptions.length === 1
          ? "CREATE_SUBSCRIPTION"
          : "MANUAL_REVIEW",
    })
  }
}
